import type { NavigationGuardNext } from "vue-router";
import { useAppStore } from "@/store";
import { useLegacyRootEvent } from "./useLegacyRootEvent";

/**
 * 編集破棄アラート / 画面遷移ガード操作（旧 `EditDiscardMixin` の composable 版）。
 *
 * `$root.$emit('edit-discard-alert' / 'on-set-navigation-guard' / 'on-set-next-route-event')`
 * を内包するため、`useLegacyRootEvent` 経由で発火する。1-E（PR-4）でこれらのイベントが
 * mitt / Pinia action へ差し替わると、本 composable も追従して変更される想定。
 *
 * 1-D（mixin → composable）。利用側の書き換えは 1-C 高リスク群（PR-3）で実施する。
 */
export function useEditDiscard() {
  const rootEvent = useLegacyRootEvent();

  /**
   * 編集状態フラグ(navigationGuard)の操作
   * @param editMode 編集状態(画面遷移をブロックするかのフラグ)
   */
  const changeEditMode = (editMode: boolean): void => {
    const navigationGuard = useAppStore().navigationGuard;
    if (navigationGuard !== editMode) {
      rootEvent.emit("on-set-navigation-guard", editMode);
    }
  };

  /**
   * 編集を破棄するかのアラートを表示させる
   * @param nextFunc アラートでOKを押した後の遷移または後処理
   */
  const showEditDiscardAlert = (nextFunc?: () => void): void => {
    const navigationGuard = useAppStore().navigationGuard;
    if (!navigationGuard) {
      if (nextFunc) nextFunc();
    } else {
      rootEvent.emit("edit-discard-alert");
      rootEvent.emit("on-set-next-route-event", nextFunc);
    }
  };

  /**
   * 掲示板内ページ離脱時にアラートを表示させる
   * @param next vue-router の NavigationGuardNext
   */
  const routeLeaveEvent = (next: NavigationGuardNext): void => {
    const navigationGuard = useAppStore().navigationGuard;
    if (!navigationGuard) {
      next();
    } else {
      rootEvent.emit("edit-discard-alert");
      rootEvent.emit("on-set-next-route-event", () => next());
      next(false);
    }
  };

  return {
    changeEditMode,
    showEditDiscardAlert,
    routeLeaveEvent,
  };
}
